import apiCaller from 'services/Api/ApiCaller';
import { API_URL } from 'settings';

export type BlueprintNewsItem = {
  id: number;
  timestamp: string;
  headline: string;
  rephrased_headline: string | null;
  display_name: string;
  body: string;
  updated_at: string | null;
  /** 'ai_curated' for an AI Curated copy. */
  source?: string;
  /** On an AI Curated copy: the headline of the original it was made from. */
  original_headline?: string;
};

export type BlueprintNewsMembership = {
  /** Whether stories can be added to or removed from this version here. */
  can_change: boolean;
  /** Why not, in words an administrator can act on. */
  reason: string | null;
  /** How many difficulties of this version share the change. */
  variants_sharing: number;
  /** The version shares its news list outside itself; the first change gives it its own. */
  separates_on_change?: boolean;
};

export type BlueprintNewsFeed = {
  variant_id: number;
  is_locked: boolean;
  start_date: string;
  end_date: string;
  items: BlueprintNewsItem[];
  membership?: BlueprintNewsMembership;
};

export type BlueprintDevPlanName = {
  node_id: number;
  position: number;
  node_code: string;
  node_name: string;
  plan_code: string;
  plan_name: string;
  plan_status: string;
  plan_hidden: boolean;
  node_active: boolean;
  name: string | null;
};

const errorText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(errorText).join(' ');
  if (value && typeof value === 'object') {
    return Object.values(value).map(errorText).join(' ');
  }
  return '';
};

async function request<T>(
  blueprintId: number,
  action: string,
  method: 'GET' | 'PATCH' | 'POST',
  data: Record<string, unknown>,
): Promise<T> {
  const response = await apiCaller({
    apiUrl: API_URL,
    path: `/blueprints/${blueprintId}/${action}`,
    method,
    data,
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      errorText(response.result) || 'The request failed. Please try again.',
    );
  }
  return response.result as T;
}

export const fetchBlueprintNews = (blueprintId: number, variantId: number) =>
  request<BlueprintNewsFeed>(blueprintId, 'news', 'GET', {
    variant_id: variantId,
  });

export const saveBlueprintNewsName = (
  blueprintId: number,
  variantId: number,
  item: BlueprintNewsItem,
  name: string,
) =>
  request<BlueprintNewsItem>(blueprintId, 'news', 'PATCH', {
    variant_id: variantId,
    news_id: item.id,
    rephrased_headline: name.trim() || null,
    expected_updated_at: item.updated_at,
  });

export const fetchBlueprintDevPlanNames = (blueprintId: number) =>
  request<{ items: BlueprintDevPlanName[] }>(
    blueprintId,
    'devplan_display_names',
    'GET',
    {},
  );

export const saveBlueprintDevPlanName = (
  blueprintId: number,
  item: BlueprintDevPlanName,
  name: string,
) =>
  request<BlueprintDevPlanName>(blueprintId, 'devplan_display_names', 'PATCH', {
    node_id: item.node_id,
    position: item.position,
    name: name.trim(),
    expected_name: item.name,
  });

/** Adds AI Curated stories to, or removes stories from, this version's news. */
export const changeBlueprintNews = (
  blueprintId: number,
  variantId: number,
  change: { add?: number[]; remove?: number[] },
) =>
  request<BlueprintNewsFeed>(blueprintId, 'news_membership', 'POST', {
    variant_id: variantId,
    add: change.add || [],
    remove: change.remove || [],
  });
